import { Skeleton, useErrorToast } from '@h2hspark/ui';
import { Check, Clock, FileText, Send, Sparkles } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { PushNotice } from '../../member/pwa/push-notice';
import { followUpEventPath } from '../follow-up/constants';

import { useYourEvents } from './api';
import {
  BROWSE_EVENTS_ACTION,
  followUpIsPending,
  followUpPillLabel,
  FOLLOW_UP_EMPTY,
  FOLLOW_UP_HEADING,
  FOLLOW_UP_ORDER,
  LIST_UNAVAILABLE,
  matchPillLabel,
  RETRY_ACTION,
  rowWhenLine,
  shortEventDate,
  UPCOMING_EMPTY,
  UPCOMING_HEADING,
  UPCOMING_ORDER,
  YOUR_EVENTS_TITLE,
  type FollowUpEventRow,
  type UpcomingEventRow,
} from './constants';
import { EventRowCard } from './event-row-card';
import { FirstEventCard } from './first-event-card';
import { NextUpCard } from './next-up-card';

/**
 * **Your Events** (`MEM-052`) — `Web App.dc.html` (10 September), `showPeople`, lines 342–398.
 *
 * The heading at `var(--text-xl)` at every width over a hairline rule, with no line beneath it —
 * Brad's cleaned design, which drops the prototype's `var(--text-4xl)` heading and its subtitle
 * (TW 44190018; Sumit, 2026-09-24, *"ok with me."*); the **Next up** card for the member's soonest
 * upcoming place (`NextUpCard`); then two columns — **My upcoming events**, soonest first, and
 * **My follow up**, latest first — each an `<h2>` at `var(--text-xl)` with its order at the right.
 * On the phone the columns stack, upcoming first (`MEM-056`'s reference table), at `md`, the one
 * width the member app changes layout at.
 *
 * ## What it replaced
 *
 * The cross-event Matches screen and the Follow up index, which were navigation items of their
 * own (Sumit, 2026-09-11, *"agree"*). Their addresses still resolve here (`MEM-052` AC-6), and the
 * per-event screens they led to are unchanged — each upcoming row opens that event's matches, each
 * follow-up row that event's follow-up.
 *
 * ## The push offer
 *
 * `MEM-040` AC-3's in-context offer used to sit on the cross-event matches screen, at the moment
 * its Notes choose: a member reading a list of people they have been matched with. That list is
 * now this page's rows, so the offer is here, with the same reason — a released match on one of
 * the member's upcoming events — and it asks for nothing until it is tapped.
 *
 * ## Where the prototype is corrected rather than transcribed (`handoff-corrections.md` #124, #126)
 *
 * Its *My follow up* lists every past event in its catalogue, registered or not; this lists the
 * events the member has a follow-up summary for, which only a checked-in member gets. Its match
 * line is fixed text; this says what is true of the member's own set. Its *"Soonest first"* and
 * *"Latest first"* are catalogue order; here they are sorts. Its relative times (*"3 weeks ago"*)
 * contradict its own dates; here each row carries its date, in the event's own timezone.
 */
export function YourEventsPage(): React.ReactElement {
  const { isPending, error, view, registrations, followUps } = useYourEvents();
  const showError = useErrorToast();

  React.useEffect(() => {
    if (error !== null) showError(error, 'We could not load your events.');
  }, [error, showError]);

  return (
    <div>
      <header className="mb-6 border-b border-border pb-4">
        <h1 className="text-xl tracking-tight">{YOUR_EVENTS_TITLE}</h1>
      </header>

      <PushNotice hasReason={view.releasedMatchTotal > 0} enabled />

      {isPending ? (
        <div className="space-y-5" aria-hidden>
          <Skeleton className="h-[220px] w-full rounded-2xl" />
          <div className="grid gap-5 md:grid-cols-2">
            <Skeleton className="h-[160px] w-full rounded-lg" />
            <Skeleton className="h-[160px] w-full rounded-lg" />
          </div>
        </div>
      ) : (
        <>
          {/* `MEM-052` AC-2: no upcoming place, no card. AC-8: in its place, the first-event
              banner — but only over a registrations read that arrived, since a failed read is
              not a member with nothing ahead (TW 44145597). */}
          {view.nextUp !== null ? (
            <div className="mb-[26px]">
              <NextUpCard event={view.nextUp} />
            </div>
          ) : registrations.failed ? null : (
            <div className="mb-[26px]">
              <FirstEventCard />
            </div>
          )}

          <div className="grid items-start gap-5 md:grid-cols-2">
            <section aria-labelledby="upcoming-heading">
              <ListHeading id="upcoming-heading" title={UPCOMING_HEADING} order={UPCOMING_ORDER} />
              <ul className="flex flex-col gap-2.5">
                {view.upcoming.map((row) => (
                  <li key={row.eventId}>
                    <UpcomingRow row={row} />
                  </li>
                ))}
              </ul>
              {/* **A read that failed is not an empty calendar** (TW 44145597). The empty state
                  and its *Browse events* are the answer to *you have nothing on*, and drawing
                  them over a failed read tells a member something false about their own week. */}
              {registrations.failed ? (
                <ListUnavailable onRetry={registrations.retry} />
              ) : view.upcoming.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-card p-[26px] text-center">
                  <p className="mb-3.5 text-md text-muted-foreground">{UPCOMING_EMPTY}</p>
                  <Link
                    to="/app"
                    className="inline-flex items-center rounded-pill border-[1.5px] border-primary bg-card px-5 py-[11px] font-brand text-sm font-bold text-primary transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {BROWSE_EVENTS_ACTION}
                  </Link>
                </div>
              ) : null}
            </section>

            <section aria-labelledby="follow-up-heading">
              <ListHeading
                id="follow-up-heading"
                title={FOLLOW_UP_HEADING}
                order={FOLLOW_UP_ORDER}
              />
              <ul className="flex flex-col gap-2.5">
                {view.followUps.map((row) => (
                  <li key={row.eventId}>
                    <FollowUpRow row={row} />
                  </li>
                ))}
              </ul>
              {/* The same rule on this side: *All followed up* is a claim about the member's
                  past evenings, and a read that did not arrive is not entitled to make it. */}
              {followUps.failed ? (
                <ListUnavailable onRetry={followUps.retry} />
              ) : view.followUps.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-card p-[26px] text-center">
                  <p className="text-md text-muted-foreground">{FOLLOW_UP_EMPTY}</p>
                </div>
              ) : null}
            </section>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A column whose read did not arrive (TW 44145597).
 *
 * The same dashed card the empty states use, so the page's shape does not jump when one of three
 * reads fails — with the column's own sentence and the read offered again. `role="status"`, because
 * this replaces a region a member was reading and the toast that fired with it is transient.
 *
 * Retrying here rather than reloading the page: the other two reads arrived and their rows are on
 * screen, and a reload would throw those away to fetch one list.
 */
function ListUnavailable({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <div
      role="status"
      className="rounded-lg border border-dashed border-border bg-card p-[26px] text-center"
    >
      <p className="mb-3.5 text-md text-muted-foreground">{LIST_UNAVAILABLE}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center rounded-pill border-[1.5px] border-primary bg-card px-5 py-[11px] font-brand text-sm font-bold text-primary transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {RETRY_ACTION}
      </button>
    </div>
  );
}

function ListHeading({
  id,
  title,
  order,
}: {
  readonly id: string;
  readonly title: string;
  readonly order: string;
}): React.ReactElement {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 id={id} className="text-xl tracking-tight">
        {title}
      </h2>
      <span className="text-sm text-muted-foreground">{order}</span>
    </div>
  );
}

/** `MEM-052` AC-4 — its date, its city and its match state, opening that event's matches. */
function UpcomingRow({ row }: { readonly row: UpcomingEventRow }): React.ReactElement {
  const released = row.matches.kind === 'released' && row.matches.count > 0;
  return (
    <EventRowCard
      to={`/app/events/${encodeURIComponent(row.eventId)}/matches`}
      tone="upcoming"
      name={row.name}
      startsAt={row.startsAt}
      timezone={row.timezone}
      when={rowWhenLine(shortEventDate(row.startsAt, row.timezone), row.city)}
      pill={{
        label: matchPillLabel(row.matches),
        tone: released ? 'brand' : 'neutral',
        Icon: released ? Sparkles : Clock,
      }}
    />
  );
}

/** `MEM-052` AC-5 — when it ran, its city, and what is left to do, opening its follow-up. */
function FollowUpRow({ row }: { readonly row: FollowUpEventRow }): React.ReactElement {
  const pending = followUpIsPending(row);
  const reduced = row.connectionCount === 0;
  return (
    <EventRowCard
      to={followUpEventPath(row.eventId)}
      tone="follow-up"
      name={row.name}
      startsAt={row.ranAt}
      timezone={row.timezone}
      when={rowWhenLine(shortEventDate(row.ranAt, row.timezone), row.city)}
      pill={{
        label: followUpPillLabel(row),
        tone: pending ? 'brand' : reduced ? 'neutral' : 'success',
        Icon: pending ? Send : reduced ? FileText : Check,
      }}
    />
  );
}
